/**
 * LPCLayerDefinitions - Manifest-driven LPC layer system
 *
 * Loads manifest.json produced by the LPC import tool and provides
 * organized access to all character customization options.
 * Equipment layers for inventory items are also resolved here.
 *
 * Path format: /lpc/spritesheets/{bodyTypePath}/{animFolder}/{variant}.png
 * The compositor receives { basePath, zPos, variant } where
 * basePath = LPC_BASE + '/' + layer.bodyTypes[bodyKey]  (trailing slash stripped)
 */

const LPC_BASE = '/lpc/spritesheets';

// Belt layers are hidden by default because no belt sprites have native run
// animations.  The walk-fallback belt covers the body's near arm during
// side-view running.  Enable via the debug panel to preview belt rendering.
let _showBelts = false;
export function setShowBelts(v) { _showBelts = !!v; }
export function getShowBelts() { return _showBelts; }

// ── Manifest + file index cache ─────────────────────────────────────
let _manifest = null;
let _manifestPromise = null;
let _itemIndex = null; // id → manifest item
let _fileIndex = null; // path → { a, v, x, f, w }

export async function loadManifest() {
  if (_manifest) return _manifest;
  if (_manifestPromise) return _manifestPromise;
  _manifestPromise = Promise.all([
    fetch('/lpc/manifest.json').then(r => r.json()),
    fetch('/lpc/fileindex.json').then(r => r.json()).catch(() => null)
  ]).then(([m, fi]) => {
    _manifest = m;
    _fileIndex = fi;
    _manifestPromise = null;
    _buildItemIndex();
    if (!fi) console.warn('LPC file index not found — sprite loading may produce 404s');
    return m;
  });
  return _manifestPromise;
}

export function getManifest() { return _manifest; }
export function getFileIndex() { return _fileIndex; }

function _buildItemIndex() {
  _itemIndex = new Map();
  if (!_manifest) return;
  for (const item of _manifest.items) {
    _itemIndex.set(item.id, item);
  }
}

function _findItem(id) {
  return _itemIndex ? _itemIndex.get(id) || null : null;
}

export function findItemById(id) {
  return _findItem(id);
}

// ── Body type map (our IDs → manifest bodyType keys) ────────────────
// Special body types (skeleton, zombie) reuse standard bodyKeys for equipment
// compatibility but use a different body item + variant for the base layer.
const BODY_TYPE_MAP = {
  'human-male':     'male',
  'human-female':   'female',
  'human-muscular': 'muscular',
  'human-teen':     'teen',
  'human-child':    'child',
  'human-pregnant': 'pregnant',
  'skeleton-male':  'male',
  'zombie-male':    'male'
};

// bodyItem/bodyVariant overrides for non-human body types
const BODY_ITEM_MAP = {
  'skeleton-male':   { item: 'body_skeleton', variant: 'skeleton' },
  'zombie-male':     { item: 'body_zombie',   variant: 'zombie' }
};

export const BODY_TYPE_LIST = [
  { id: 'human-male',     name: 'Male',     bodyKey: 'male' },
  { id: 'human-female',   name: 'Female',   bodyKey: 'female' },
  { id: 'human-muscular', name: 'Muscular', bodyKey: 'muscular' },
  { id: 'human-teen',     name: 'Teen',     bodyKey: 'teen' },
  { id: 'human-child',    name: 'Child',    bodyKey: 'child' },
  { id: 'human-pregnant', name: 'Pregnant', bodyKey: 'pregnant' },
  { id: 'skeleton-male',  name: 'Skeleton',  bodyKey: 'male' },
  { id: 'zombie-male',    name: 'Zombie',    bodyKey: 'male' },
];

// ── Skin color swatches ─────────────────────────────────────────────
export const SKIN_SWATCHES = {
  'light': '#ffd5b8', 'amber': '#e8a870', 'olive': '#c4a060',
  'taupe': '#b08060', 'bronze': '#a06830', 'brown': '#7a4820',
  'black': '#3a2010', 'lavender': '#c8a8d8', 'blue': '#6890c0',
  'green': '#60a060', 'dark_green': '#306030', 'zombie_green': '#80b060',
  'pale_green': '#a0d0a0', 'bright_green': '#40d040',
  'fur_black': '#2a2a2a', 'fur_brown': '#6b4226', 'fur_tan': '#c4a46c',
  'fur_copper': '#b06830', 'fur_gold': '#d4a830', 'fur_grey': '#888888',
  'fur_white': '#e8e0d8'
};

// ── Hair color swatches ─────────────────────────────────────────────
// Keys match manifest variant names (spaces, not underscores — path sanitization in _addItemLayers)
export const HAIR_SWATCHES = {
  'blonde': '#e8d070', 'ash': '#c0b090', 'sandy': '#d4b870',
  'platinum': '#e0d8c0', 'strawberry': '#d49060', 'redhead': '#a04020',
  'ginger': '#c06830', 'carrot': '#e07030', 'chestnut': '#6a3818',
  'light brown': '#8a5828', 'dark brown': '#4a2810', 'black': '#1a1a1a',
  'raven': '#2a1a2a', 'dark gray': '#606060', 'gray': '#909090',
  'white': '#e8e8e8', 'red': '#b03020', 'orange': '#d07020',
  'gold': '#d4a020', 'green': '#308030', 'blue': '#4080c0',
  'navy': '#203060', 'violet': '#6030a0', 'purple': '#8040a0',
  'pink': '#e080a0', 'rose': '#d06080'
};

// ── Eye color swatches ──────────────────────────────────────────────
export const EYE_SWATCHES = {
  'blue': '#4488cc', 'brown': '#8b5e3c', 'gray': '#888888',
  'green': '#448844', 'orange': '#cc8844', 'purple': '#884488',
  'red': '#cc4444', 'yellow': '#cccc44'
};

// ── General variant colors (metals, fabrics, gems, etc.) ────────────
// Fallback swatch palette for items whose variants don't match skin/hair/eye.
export const VARIANT_SWATCHES = {
  // Metals
  'steel': '#8a9ba8', 'iron': '#6b6b6b', 'ceramic': '#d4cfc8',
  'brass': '#b5a642', 'copper': '#b87333', 'bronze': '#a0784a',
  'silver': '#c0c0c0', 'gold': '#daa520',
  // Fabrics / materials
  'black': '#1a1a1a', 'blue': '#4466aa', 'bluegray': '#6080a0',
  'brown': '#6b4226', 'charcoal': '#363636', 'forest': '#2a5a2a',
  'gray': '#808080', 'green': '#3a8a3a', 'lavender': '#9878b8',
  'leather': '#8b5e3c', 'maroon': '#6b1a2a', 'navy': '#1a2a50',
  'orange': '#cc7722', 'pink': '#d07090', 'purple': '#7040a0',
  'red': '#b83030', 'rose': '#c06070', 'sky': '#6aacdc',
  'slate': '#5a6a7a', 'tan': '#c4a46c', 'teal': '#2a8a7a',
  'walnut': '#5a3a20', 'white': '#e8e8e8', 'yellow': '#ccaa22',
  'dark': '#2a2a2a', 'base': '#7a7a7a', 'sunglasses': '#1a1a2a'
};

// ── Creator categories (for the character creator UI) ───────────────
// Each category defines which manifest item prefixes belong to it,
// display name, and how variants are colored.
export const CREATOR_CATEGORIES = {
  body: {
    tab: 'Body', label: 'Body Type',
    filter: id => id === 'body',
    hideItemPicker: true // body type is chosen via BODY_TYPE_LIST
  },
  head: {
    tab: 'Face', label: 'Head Shape',
    filter: id => id.startsWith('heads_'),
    groups: [
      { name: 'Human', filter: id => id.startsWith('heads_human') },
      { name: 'Fantasy', filter: id => /^heads_(boarman|minotaur|wartotaur|goblin|orc|troll|wolf)/.test(id) },
      { name: 'Animal', filter: id => /^heads_(mouse|pig|rabbit|rat|sheep|lizard)/.test(id) },
      { name: 'Other', filter: id => /^heads_(alien|frankenstein|jack|skeleton|vampire|zombie)/.test(id) }
    ]
  },
  face: {
    tab: 'Face', label: 'Expression',
    // Only global faces work (non-template paths)
    filter: id => id.startsWith('face_') && !_hasTemplatePath(id)
  },
  eyes: {
    tab: 'Face', label: 'Eyes',
    filter: id => id === 'eye_color' || id.startsWith('eyes_'),
    variantType: 'eye'
  },
  eyebrows: {
    tab: 'Face', label: 'Eyebrows',
    filter: id => id.startsWith('eyebrows_'),
    variantType: 'hair'
  },
  ears: {
    tab: 'Features', label: 'Ears',
    filter: id => id.startsWith('head_ears_')
  },
  nose: {
    tab: 'Features', label: 'Nose',
    filter: id => id.startsWith('head_nose_')
  },
  hair: {
    tab: 'Hair', label: 'Hair Style',
    filter: id => id.startsWith('hair_'),
    variantType: 'hair'
  },
  hairext: {
    tab: 'Hair', label: 'Hair Extension',
    filter: id => id.startsWith('hairext_'),
    variantType: 'hair'
  },
  beard: {
    tab: 'Hair', label: 'Facial Hair',
    filter: id => id.startsWith('beards_'),
    variantType: 'hair'
  },
  facial: {
    tab: 'Features', label: 'Facial Accessories',
    filter: id => id.startsWith('facial_')
  },
  horns: {
    tab: 'Extras', label: 'Horns & Fins',
    filter: id => id.startsWith('head_horns_') || id.startsWith('head_fins_')
  },
  tail: {
    tab: 'Extras', label: 'Tail',
    filter: id => id.startsWith('tail_') && !_isPlaceholder(id)
  },
  wings: {
    tab: 'Extras', label: 'Wings',
    filter: id => id.startsWith('wings_')
  }
};

function _isPlaceholder(itemId) {
  const item = _findItem(itemId);
  return item && item.name && /placeholder/i.test(item.name);
}

function _hasTemplatePath(itemId) {
  const item = _findItem(itemId);
  if (!item) return false;
  for (const layer of item.layers) {
    for (const path of Object.values(layer.bodyTypes)) {
      if (path.indexOf('${') >= 0) return true;
    }
  }
  return false;
}

// ── Query helpers for the character creator ──────────────────────────

/**
 * Get all manifest items for a creator category, filtered by body type compatibility.
 * @param {string} categoryKey - Key from CREATOR_CATEGORIES
 * @param {string} [bodyKey='male'] - Manifest body type key
 * @returns {{ id: string, name: string, item: Object }[]}
 */
export function getItemsForCategory(categoryKey, bodyKey = 'male') {
  if (!_manifest) return [];
  const cat = CREATOR_CATEGORIES[categoryKey];
  if (!cat) return [];

  const results = [];
  for (const item of _manifest.items) {
    if (!cat.filter(item.id)) continue;
    // Check body type compatibility
    if (item.layers.length > 0) {
      const hasBody = item.layers.some(l => l.bodyTypes[bodyKey]);
      if (!hasBody) continue;
    }
    // Skip template-path items
    if (_hasTemplatePath(item.id)) continue;

    results.push({
      id: item.id,
      name: _formatItemName(item),
      item
    });
  }

  return results;
}

/**
 * Get available variants for a manifest item.
 * @param {string} itemId
 * @returns {string[]}
 */
export function getVariantsForItem(itemId) {
  const item = _findItem(itemId);
  return item ? (item.variants || []) : [];
}

/**
 * Check if a manifest item has layers compatible with the given body type.
 * @param {string} itemId
 * @param {string} bodyKey - Manifest body type key (e.g., 'male', 'child')
 * @returns {boolean}
 */
export function isItemCompatibleWithBody(itemId, bodyKey) {
  const item = _findItem(itemId);
  if (!item) return false;
  return item.layers.some(l => l.bodyTypes[bodyKey]);
}

/**
 * Get skin color variants from the body item.
 */
export function getSkinVariants() {
  const body = _findItem('body');
  return body ? body.variants : Object.keys(SKIN_SWATCHES);
}

function _formatItemName(item) {
  // Turn 'hair_bangs' into 'Bangs', 'heads_human_male' into 'Human Male'
  let name = item.name || item.id;
  // Remove common prefixes
  name = name
    .replace(/^(hair|heads|head|eyes|eyebrows|beards|facial|tail|wings|face|hat|torso|legs|feet|neck|belt|cape|shield|weapon|tool|hairext)[\s_]/i, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
  return name;
}

// ── buildLayerStack ─────────────────────────────────────────────────

/**
 * Build the layer stack for the compositor from appearance + equipment.
 *
 * @param {Object} appearance - Character appearance config
 * @param {Object} equippedItems - Map of slot → { lpcLayer, lpcVariant }
 * @returns {LayerDef[]} Array of { basePath, zPos, variant }
 */
export function buildLayerStack(appearance, equippedItems) {
  if (!_manifest) return [];

  const bodyKey = BODY_TYPE_MAP[appearance.bodyType] || 'male';
  const skinColor = appearance.skinColor || 'light';
  const hairColor = appearance.hairColor || 'dark brown';
  const layers = [];

  // Resolve the correct variant for an item:
  //  - If appearance has an explicit variant override (e.g., earsVariant), use it
  //  - If item.matchBodyColor, use skin color
  //  - Otherwise use item's first variant, or the fallback
  function resolveVariant(itemId, overrideKey, fallback) {
    if (overrideKey && appearance[overrideKey]) return appearance[overrideKey];
    const item = _findItem(itemId);
    if (!item) return fallback;
    if (item.matchBodyColor) return skinColor;
    // Non-matchBodyColor: use first available variant or fallback
    if (item.variants && item.variants.length > 0) return item.variants[0];
    return fallback;
  }

  // 1. Body base (skeleton/zombie use their own body item + variant)
  const bodyOverride = BODY_ITEM_MAP[appearance.bodyType];
  const bodyStartIdx = layers.length;
  if (bodyOverride) {
    _addItemLayers(layers, bodyOverride.item, bodyKey, bodyOverride.variant);
  } else {
    _addItemLayers(layers, 'body', bodyKey, skinColor);
  }
  // Tag body layers so the compositor can identify them for fallback sync
  for (let i = bodyStartIdx; i < layers.length; i++) {
    layers[i].isBody = true;
  }

  // 2. Head shape
  if (appearance.head) {
    _addItemLayers(layers, appearance.head, bodyKey, resolveVariant(appearance.head, 'headVariant', skinColor));
  }

  // 3. Face expression
  if (appearance.face) {
    _addItemLayers(layers, appearance.face, bodyKey, resolveVariant(appearance.face, null, skinColor));
  }

  // 4. Eyes
  if (appearance.eyes) {
    _addItemLayers(layers, appearance.eyes, bodyKey, appearance.eyeColor || 'blue');
  }

  // 5. Eyebrows
  if (appearance.eyebrows) {
    _addItemLayers(layers, appearance.eyebrows, bodyKey, appearance.eyebrowColor || hairColor);
  }

  // 6. Ears (some matchBodyColor→skin, others use hair-like colors)
  if (appearance.ears) {
    _addItemLayers(layers, appearance.ears, bodyKey, resolveVariant(appearance.ears, 'earsVariant', hairColor));
  }

  // 7. Nose
  if (appearance.nose) {
    _addItemLayers(layers, appearance.nose, bodyKey, resolveVariant(appearance.nose, null, skinColor));
  }

  // Check if a head-slot item (helmet/hat/hood) is equipped — if so, suppress
  // hair and hair extensions to prevent large hairstyles from clipping through.
  const hasHeadwear = equippedItems && Object.values(equippedItems).some(
    item => item && item.slot === 'head'
  );

  // 8. Hair (skip when headwear is equipped)
  if (appearance.hair && !hasHeadwear) {
    _addItemLayers(layers, appearance.hair, bodyKey, hairColor);
  }

  // 9. Hair extension (skip when headwear is equipped)
  if (appearance.hairext && !hasHeadwear) {
    _addItemLayers(layers, appearance.hairext, bodyKey, hairColor);
  }

  // 10. Beard
  if (appearance.beard) {
    _addItemLayers(layers, appearance.beard, bodyKey, appearance.beardColor || hairColor);
  }

  // 11. Facial accessories
  if (appearance.facial) {
    const facialItem = _findItem(appearance.facial);
    const defaultVar = facialItem && facialItem.variants && facialItem.variants[0]
      ? facialItem.variants[0] : 'default';
    _addItemLayers(layers, appearance.facial, bodyKey, appearance.facialVariant || defaultVar);
  }

  // 12. Horns/Fins
  if (appearance.horns) {
    _addItemLayers(layers, appearance.horns, bodyKey, resolveVariant(appearance.horns, 'hornsVariant', skinColor));
  }

  // 13. Tail
  if (appearance.tail) {
    _addItemLayers(layers, appearance.tail, bodyKey, resolveVariant(appearance.tail, 'tailVariant', skinColor));
  }

  // 14. Wings
  if (appearance.wings) {
    _addItemLayers(layers, appearance.wings, bodyKey, resolveVariant(appearance.wings, 'wingsVariant', skinColor));
  }

  // 15. Equipment layers from inventory
  if (equippedItems) {
    for (const item of Object.values(equippedItems)) {
      if (!item || !item.lpcLayer) continue;
      // Support single layer or array of layers (e.g., cape + buckle)
      const entries = Array.isArray(item.lpcLayer) ? item.lpcLayer : [item.lpcLayer];
      for (const entry of entries) {
        const layerId = typeof entry === 'string' ? entry : entry.layerId;
        const variant = typeof entry === 'string'
          ? (item.lpcVariant || 'default')
          : (entry.variant || 'default');
        _addItemLayers(layers, layerId, bodyKey, variant);
      }
    }
  }

  return layers;
}

function _addItemLayers(layers, itemId, bodyKey, variant) {
  const item = _findItem(itemId);
  if (!item) return;

  // Boomerang weapon layer is rendered as a detached world-space sprite
  // (see Game._spawnDetachedWeaponAnim), so exclude it from the composite.
  if (itemId === 'weapon_ranged_boomerang') return;

  // Skip belt/buckle layers when belt display is disabled (default).
  // Belt sprites lack run animations; the walk-fallback covers the near arm.
  if (!_showBelts && (itemId.startsWith('belt_') || itemId === 'buckles')) return;

  for (const layer of item.layers) {
    let path = layer.bodyTypes[bodyKey];
    if (!path) {
      // Fallback: try first available body type
      const keys = Object.keys(layer.bodyTypes);
      if (keys.length > 0) path = layer.bodyTypes[keys[0]];
    }
    if (!path) continue;

    // Strip trailing slash
    path = path.replace(/\/$/, '');

    // Skip template paths — they need head/expression substitution we can't resolve yet
    if (path.indexOf('${') >= 0) continue;

    let zPos = layer.zPos;

    // Tail front layers should render above wings (105-106) but below
    // shield fg (110+).  Manifest values are inconsistent (85 for reptile
    // tails, 125 for furry tails), so normalise them all to 107.
    if (itemId.startsWith('tail_') && zPos > 80) {
      zPos = 107;
    }

    const layerDef = {
      basePath: LPC_BASE + '/' + path,
      zPos,
      variant: variant.replace(/ /g, '_')
    };

    // Tag layers so the compositor can adjust draw order when viewed
    // from behind (north-facing directions).
    if (itemId.startsWith('tail_')) layerDef.isTail = true;
    if (itemId.startsWith('wings_')) layerDef.isWing = true;
    if (itemId.startsWith('shield_')) layerDef.isShield = true;

    // Restrict attack-specific layers to their matching animations only.
    // Without this, the flat-file fallback path in _loadLayerImages accidentally
    // loads oversize attack spritesheets (e.g., attack_slash/longsword.png) for
    // walk/idle, causing garbage weapon fragments to render.
    // Each variant renders ONLY during its specific LPC animation — stripping the
    // size suffix (_oversize, _128) gives the precise animation name.
    if (layer.customAnimation) {
      const animName = layer.customAnimation.replace(/_(oversize|128)$/, '');
      if      (animName === 'slash')         layerDef.animations = ['slash'];
      else if (animName === 'slash_reverse') layerDef.animations = ['slash_reverse'];
      else if (animName === 'backslash')     layerDef.animations = ['backslash'];
      else if (animName === 'halfslash')     layerDef.animations = ['halfslash'];
      else if (animName === 'thrust')        layerDef.animations = ['thrust'];
      else if (animName === 'spellcast')     layerDef.animations = ['spellcast'];
      else if (animName === 'shoot')         layerDef.animations = ['shoot'];
      // walk_128: restrict to movement/idle — must NOT render during attacks
      // (would overlay walk-pose weapon on top of attack-specific weapon sprites)
      else if (animName === 'walk')          layerDef.animations = ['walk', 'idle', 'run', 'jump', 'hurt', 'sit', 'climb', 'emote', 'combat_idle'];
    }

    layers.push(layerDef);
  }
}

// ── Backward-compat exports for old CharacterCreator ────────────────
// These are referenced by CharacterSprite and PlayerSync via buildLayerStack
// which is the main export. The old named exports are kept for migration.

export const BODY_TYPES = {}; // deprecated — use BODY_TYPE_LIST + manifest
export const HAIR_STYLES = {}; // deprecated — use getItemsForCategory('hair')
export const EYE_STYLES = {};  // deprecated — use getItemsForCategory('eyes')

export function getBodyTypeOptions() { return BODY_TYPE_LIST; }

export function getHairStyleOptions() {
  return getItemsForCategory('hair').map(i => ({ id: i.id, name: i.name }));
}

export function getEyeStyleOptions() {
  return getItemsForCategory('eyes').map(i => ({ id: i.id, name: i.name }));
}
